import React, {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from 'react';

interface WidgetExportContextValue {
	/** Called by ReportCsvExportButton to register its export function */
	registerExport: (exportFn: () => void) => void;
}

const WidgetExportContext = createContext<WidgetExportContextValue | null>(
	null,
);

interface WidgetExportProviderProps {
	children: React.ReactNode;
	/** Called when a report component registers an export function */
	onExportRegistered: (exportFn: (() => void) | null) => void;
}

/**
 * Provides a context for report components to register their CSV export function
 * with the WidgetContainer, so the export action appears in the widget's "..." menu
 * instead of as a standalone button inside the widget content.
 */
export function WidgetExportProvider({
	children,
	onExportRegistered,
}: WidgetExportProviderProps): React.ReactElement {
	// Use ref to keep a stable callback reference for the context value
	const onExportRegisteredRef = useRef(onExportRegistered);
	onExportRegisteredRef.current = onExportRegistered;

	const registerExport = useCallback((exportFn: () => void) => {
		onExportRegisteredRef.current(exportFn);
	}, []);

	// Stable context value to avoid unnecessary re-renders
	const [contextValue] = useState<WidgetExportContextValue>(() => ({
		registerExport,
	}));

	return (
		<WidgetExportContext.Provider value={contextValue}>
			{children}
		</WidgetExportContext.Provider>
	);
}

/**
 * Returns the widget export context if inside a WidgetExportProvider, null otherwise.
 * Used by ReportCsvExportButton to decide whether to render as a button or
 * register its export function with the parent widget.
 */
export function useWidgetExport(): WidgetExportContextValue | null {
	return useContext(WidgetExportContext);
}
